/**
 * Tag Expression Parser and Evaluator
 * 
 * Supports SQL-like syntax for tag filtering:
 * - "TAG1" AND "TAG2" - both must be present
 * - "TAG1" OR "TAG2" - at least one must be present
 * - NOT "TAG1" - must not be present
 * - Parentheses for grouping: ("TAG1" OR "TAG2") AND NOT "TAG3"
 * 
 * Grammar:
 * expression := term ((OR | AND) term)*
 * term       := NOT? factor
 * factor     := ( expression ) | tag
 * tag        := "identifier"
 */

// Token types
type TokenType = 'AND' | 'OR' | 'NOT' | 'LPAREN' | 'RPAREN' | 'TAG' | 'EOF'

interface Token {
    type: TokenType
    value: string
    position: number
}

// AST Node types
export type ASTNode =
    | { type: 'tag'; value: string }
    | { type: 'not'; operand: ASTNode }
    | { type: 'and'; left: ASTNode; right: ASTNode }
    | { type: 'or'; left: ASTNode; right: ASTNode }

export type ParseResult = ParseResultSuccess | ParseResultError

export interface ParseResultSuccess {
    success: true
    ast: ASTNode
}

export interface ParseResultError {
    success: false
    error: string
    position?: number
}

export interface ValidationResult {
    valid: boolean
    error?: string
    position?: number
}

export interface StructuredTags {
    required: string[]      // AND logic - all must be present
    requiredAny: string[]   // OR logic - at least one must be present
    excluded: string[]      // NOT logic - none must be present
    isComplex: boolean      // True if expression can't be represented as simple structured format
}

/**
 * Tokenize input expression
 */
function tokenize(input: string): Token[] {
    const tokens: Token[] = []
    let pos = 0

    while (pos < input.length) {
        // Skip whitespace
        while (pos < input.length && /\s/.test(input[pos])) {
            pos++
        }

        if (pos >= input.length) break

        const startPos = pos
        const char = input[pos]

        // Parentheses
        if (char === '(') {
            tokens.push({ type: 'LPAREN', value: '(', position: startPos })
            pos++
            continue
        }

        if (char === ')') {
            tokens.push({ type: 'RPAREN', value: ')', position: startPos })
            pos++
            continue
        }

        // Quoted tag
        if (char === '"') {
            pos++
            let value = ''
            while (pos < input.length && input[pos] !== '"') {
                if (input[pos] === '\\' && pos + 1 < input.length) {
                    pos++
                    value += input[pos]
                } else {
                    value += input[pos]
                }
                pos++
            }
            if (pos >= input.length) {
                throw new Error(`Unterminated string at position ${startPos}`)
            }
            pos++ // skip closing quote
            tokens.push({ type: 'TAG', value: value.toLowerCase(), position: startPos })
            continue
        }

        // Keywords or unquoted identifiers
        if (/[a-zA-Z_]/.test(char)) {
            let word = ''
            while (pos < input.length && /[a-zA-Z0-9_]/.test(input[pos])) {
                word += input[pos]
                pos++
            }

            const upper = word.toUpperCase()
            if (upper === 'AND') {
                tokens.push({ type: 'AND', value: 'AND', position: startPos })
            } else if (upper === 'OR') {
                tokens.push({ type: 'OR', value: 'OR', position: startPos })
            } else if (upper === 'NOT') {
                tokens.push({ type: 'NOT', value: 'NOT', position: startPos })
            } else {
                // Treat as unquoted tag
                tokens.push({ type: 'TAG', value: word.toLowerCase(), position: startPos })
            }
            continue
        }

        throw new Error(`Unexpected character '${char}' at position ${pos}`)
    }

    tokens.push({ type: 'EOF', value: '', position: pos })
    return tokens
}

/**
 * Parser class for building AST
 */
class Parser {
    private tokens: Token[]
    private current: number = 0

    constructor(tokens: Token[]) {
        this.tokens = tokens
    }

    parse(): ASTNode {
        const result = this.parseExpression()
        if (this.peek().type !== 'EOF') {
            throw new Error(`Unexpected token '${this.peek().value}' at position ${this.peek().position}`)
        }
        return result
    }

    private peek(): Token {
        return this.tokens[this.current]
    }

    private advance(): Token {
        const token = this.tokens[this.current]
        if (token.type !== 'EOF') {
            this.current++
        }
        return token
    }

    private parseExpression(): ASTNode {
        let left = this.parseTerm()

        while (this.peek().type === 'AND' || this.peek().type === 'OR') {
            const op = this.advance()
            const right = this.parseTerm()
            if (op.type === 'AND') {
                left = { type: 'and', left, right }
            } else {
                left = { type: 'or', left, right }
            }
        }

        return left
    }

    private parseTerm(): ASTNode {
        if (this.peek().type === 'NOT') {
            this.advance()
            const operand = this.parseFactor()
            return { type: 'not', operand }
        }
        return this.parseFactor()
    }

    private parseFactor(): ASTNode {
        const token = this.peek()

        if (token.type === 'LPAREN') {
            this.advance()
            const expr = this.parseExpression()
            if (this.peek().type !== 'RPAREN') {
                throw new Error(`Expected ')' at position ${this.peek().position}`)
            }
            this.advance()
            return expr
        }

        if (token.type === 'TAG') {
            this.advance()
            return { type: 'tag', value: token.value }
        }

        throw new Error(`Expected tag or '(' at position ${token.position}`)
    }
}

/**
 * Parse a tag expression string into an AST
 */
export function parseExpression(input: string): ParseResult {
    if (!input || !input.trim()) {
        return { success: false, error: 'Expression cannot be empty' }
    }

    try {
        const tokens = tokenize(input)
        const parser = new Parser(tokens)
        const ast = parser.parse()
        return { success: true, ast }
    } catch (err) {
        const error = err instanceof Error ? err.message : 'Unknown parse error'
        const posMatch = error.match(/position (\d+)/)
        return {
            success: false,
            error,
            position: posMatch ? parseInt(posMatch[1], 10) : undefined,
        }
    }
}

/**
 * Validate an expression string
 */
export function validateExpression(input: string): ValidationResult {
    if (!input || !input.trim()) {
        return { valid: true } // Empty is valid (no filtering)
    }

    const result = parseExpression(input)
    if (result.success) {
        return { valid: true }
    }
    return { valid: false, error: result.error, position: result.position }
}

/**
 * Evaluate an AST against a set of symbol tags
 */
export function evaluateAST(ast: ASTNode, symbolTags: string[]): boolean {
    const normalizedTags = new Set(symbolTags.map(t => t.toLowerCase()))

    function evaluate(node: ASTNode): boolean {
        switch (node.type) {
            case 'tag':
                return normalizedTags.has(node.value)
            case 'not':
                return !evaluate(node.operand)
            case 'and':
                return evaluate(node.left) && evaluate(node.right)
            case 'or':
                return evaluate(node.left) || evaluate(node.right)
        }
    }

    return evaluate(ast)
}

/**
 * Evaluate an expression string against symbol tags
 */
export function evaluateExpression(input: string, symbolTags: string[]): boolean {
    if (!input || !input.trim()) {
        return true // Empty expression matches everything
    }

    const result = parseExpression(input)
    if (!result.success) {
        console.warn('Invalid tag expression:', result.error)
        return true // Invalid expressions default to matching
    }

    return evaluateAST(result.ast, symbolTags)
}

/**
 * Extract all tag names from an AST
 */
export function extractTags(ast: ASTNode): { included: string[]; excluded: string[] } {
    const included: string[] = []
    const excluded: string[] = []

    function walk(node: ASTNode, negated: boolean = false): void {
        switch (node.type) {
            case 'tag':
                if (negated) {
                    excluded.push(node.value)
                } else {
                    included.push(node.value)
                }
                break
            case 'not':
                walk(node.operand, !negated)
                break
            case 'and':
            case 'or':
                walk(node.left, negated)
                walk(node.right, negated)
                break
        }
    }

    walk(ast)

    return {
        included: [...new Set(included)],
        excluded: [...new Set(excluded)],
    }
}

/**
 * Try to convert AST to structured tag format (for simple expressions)
 * Returns isComplex: true if expression can't be simply represented
 */
export function astToStructured(ast: ASTNode): StructuredTags {
    const result: StructuredTags = {
        required: [],
        requiredAny: [],
        excluded: [],
        isComplex: false,
    }

    // Check if it's a simple pattern we can represent
    function analyzeSimple(node: ASTNode): 'required' | 'requiredAny' | 'excluded' | 'complex' {
        if (node.type === 'tag') {
            return 'required'
        }

        if (node.type === 'not' && node.operand.type === 'tag') {
            return 'excluded'
        }

        // Pure AND of tags (required)
        if (node.type === 'and') {
            const leftType = analyzeSimple(node.left)
            const rightType = analyzeSimple(node.right)
            if (leftType === 'required' && rightType === 'required') {
                return 'required'
            }
            if (leftType === 'excluded' && rightType === 'excluded') {
                return 'excluded'
            }
            if ((leftType === 'required' || leftType === 'excluded') &&
                (rightType === 'required' || rightType === 'excluded')) {
                return 'complex' // Mix of required and excluded at same level
            }
        }

        // Pure OR of tags (requiredAny)
        if (node.type === 'or') {
            const leftType = analyzeSimple(node.left)
            const rightType = analyzeSimple(node.right)
            if ((leftType === 'required' || leftType === 'requiredAny') &&
                (rightType === 'required' || rightType === 'requiredAny')) {
                return 'requiredAny'
            }
        }

        return 'complex'
    }

    function collectTags(node: ASTNode, bucket: 'required' | 'requiredAny' | 'excluded'): void {
        if (node.type === 'tag') {
            if (bucket === 'required') result.required.push(node.value)
            else if (bucket === 'requiredAny') result.requiredAny.push(node.value)
            return
        }

        if (node.type === 'not' && node.operand.type === 'tag') {
            result.excluded.push(node.operand.value)
            return
        }

        if (node.type === 'and' || node.type === 'or') {
            const targetBucket = node.type === 'or' ? 'requiredAny' : bucket
            collectTags(node.left, targetBucket)
            collectTags(node.right, targetBucket)
        }
    }

    const analysisResult = analyzeSimple(ast)
    if (analysisResult === 'complex') {
        // Fall back to extracting tags without structure
        const { included, excluded } = extractTags(ast)
        result.required = included
        result.excluded = excluded
        result.isComplex = true
    } else {
        collectTags(ast, analysisResult === 'requiredAny' ? 'requiredAny' : 'required')
    }

    return result
}

/**
 * Convert structured tags to expression string
 */
export function structuredToExpression(structured: {
    required?: string[]
    requiredAny?: string[]
    excluded?: string[]
}): string {
    const parts: string[] = []

    // Required tags (AND)
    if (structured.required && structured.required.length > 0) {
        const reqPart = structured.required.map(t => `"${t}"`).join(' AND ')
        parts.push(structured.required.length > 1 ? `(${reqPart})` : reqPart)
    }

    // Required any tags (OR)
    if (structured.requiredAny && structured.requiredAny.length > 0) {
        const anyPart = structured.requiredAny.map(t => `"${t}"`).join(' OR ')
        parts.push(`(${anyPart})`)
    }

    // Excluded tags
    if (structured.excluded && structured.excluded.length > 0) {
        for (const tag of structured.excluded) {
            parts.push(`NOT "${tag}"`)
        }
    }

    return parts.join(' AND ')
}

/**
 * Convert legacy tag fields to expression string
 */
export function legacyTagsToExpression(
    requiredTags?: string[],
    requiredTagsAny?: string[],
    excludeTags?: string[]
): string {
    return structuredToExpression({
        required: requiredTags,
        requiredAny: requiredTagsAny,
        excluded: excludeTags,
    })
}

/**
 * Format expression for display with syntax highlighting info
 */
export interface FormattedToken {
    text: string
    type: 'keyword' | 'tag' | 'operator' | 'paren' | 'whitespace'
}

export function formatExpression(input: string): FormattedToken[] {
    if (!input.trim()) return []

    const result: FormattedToken[] = []
    let pos = 0

    while (pos < input.length) {
        // Whitespace
        if (/\s/.test(input[pos])) {
            let ws = ''
            while (pos < input.length && /\s/.test(input[pos])) {
                ws += input[pos]
                pos++
            }
            result.push({ text: ws, type: 'whitespace' })
            continue
        }

        // Parentheses
        if (input[pos] === '(' || input[pos] === ')') {
            result.push({ text: input[pos], type: 'paren' })
            pos++
            continue
        }

        // Quoted string
        if (input[pos] === '"') {
            let str = '"'
            pos++
            while (pos < input.length && input[pos] !== '"') {
                if (input[pos] === '\\' && pos + 1 < input.length) {
                    str += input[pos]
                    pos++
                }
                str += input[pos]
                pos++
            }
            if (pos < input.length) {
                str += '"'
                pos++
            }
            result.push({ text: str, type: 'tag' })
            continue
        }

        // Words
        if (/[a-zA-Z_]/.test(input[pos])) {
            let word = ''
            while (pos < input.length && /[a-zA-Z0-9_]/.test(input[pos])) {
                word += input[pos]
                pos++
            }
            const upper = word.toUpperCase()
            if (upper === 'AND' || upper === 'OR' || upper === 'NOT') {
                result.push({ text: word, type: 'keyword' })
            } else {
                result.push({ text: word, type: 'tag' })
            }
            continue
        }

        // Unknown character
        result.push({ text: input[pos], type: 'operator' })
        pos++
    }

    return result
}
